import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check } from "lucide-react";
import { HeartRain } from "@/components/HeartRain";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Footer } from "@/components/Footer";
import { useI18n } from "@/context/LanguageContext";

const CopyRow = ({ label, value, testId, copyText, copiedText }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
        return;
      }
    } catch {
      /* fall through */
    }
    try {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      ta.setAttribute("readonly", "");
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, value.length);
      document.execCommand("copy");
      document.body.removeChild(ta);
    } catch (err) {
      console.error("Copy fallback failed", err);
    }
  };
  return (
    <div
      data-testid={testId}
      className="flex flex-col md:flex-row md:items-center justify-between gap-3 py-5 border-b border-[#141518]/15 last:border-b-0"
    >
      <div className="flex-1 min-w-0">
        <div className="font-condensed font-bold uppercase tracking-[0.25em] text-sm md:text-base text-[#F0311B]">
          {label}
        </div>
        <div className="font-display font-semibold text-base sm:text-xl md:text-2xl mt-1 break-all text-[#141518]">
          {value}
        </div>
      </div>
      <button
        onClick={onCopy}
        data-testid={`${testId}-copy`}
        className="self-start md:self-auto inline-flex items-center justify-center gap-2 px-4 py-2 border-2 border-[#141518] text-xs uppercase tracking-[0.2em] font-bold shadow-hard-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0_0_#141518] hover:bg-[#7AD5DC] transition-all bg-white text-[#141518] whitespace-nowrap"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
        {copied ? copiedText : copyText}
      </button>
    </div>
  );
};

const MiniNav = () => {
  const { content } = useI18n();
  return (
    <header
      data-testid="regal-nav"
      className="fixed top-0 left-0 right-0 z-50 bg-[#F0311B] text-white shadow-[0_2px_0_0_#141518]"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex items-center justify-between h-14 md:h-20">
        <a href="/" data-testid="regal-nav-home" className="flex items-center" aria-label="Ariadna & Wojtek">
          <img
            src={content.logo}
            alt="Ariadna & Wojtek"
            className="h-9 md:h-12 w-auto select-none"
            draggable="false"
          />
        </a>
        <div className="flex items-center gap-2 md:gap-3">
          <LanguageSwitcher />
          <a
            href="/"
            data-testid="regal-nav-back"
            className="inline-flex items-center gap-2 bg-white text-[#141518] px-3 md:px-5 py-2 md:py-2.5 text-[10px] md:text-[11px] uppercase tracking-[0.18em] md:tracking-[0.22em] font-bold border-2 border-[#141518] hover:bg-[#7AD5DC] transition-colors"
          >
            ← Web
          </a>
        </div>
      </div>
    </header>
  );
};

export const RegalPage = () => {
  const { content, t } = useI18n();
  const g = t.gift;
  const gift = content.gift;

  return (
    <div className="App grain min-h-screen bg-[#FFF7F1] flex flex-col" data-testid="regal-page">
      <MiniNav />

      {/* Spacer for fixed nav */}
      <div className="h-14 md:h-20" />

      {/* HERO */}
      <section
        data-testid="regal-hero"
        className="relative overflow-hidden bg-[#FDECD7] border-b-2 border-[#141518] py-16 md:py-28"
      >
        <HeartRain density={70} />
        <div className="relative z-20 max-w-[1000px] mx-auto px-4 md:px-8 text-center">
          <div className="font-condensed font-bold uppercase tracking-[0.35em] text-xs md:text-sm text-[#F0311B]">
            ✱ {g.eyebrow} ✱
          </div>
          <h1
            data-testid="regal-title"
            className="mt-6 font-display font-bold text-[15vw] sm:text-7xl md:text-8xl leading-[0.88] text-[#141518]"
          >
            {g.titleA} <br />
            <span className="italic text-[#F0311B]">{g.titleB}</span>
          </h1>
          <p className="mt-8 mx-auto max-w-[640px] font-serif text-[#141518] text-base md:text-xl leading-relaxed">
            {gift.intro}
          </p>
        </div>
      </section>

      {/* GIFT DETAILS */}
      <section
        data-testid="regal-details"
        className="relative bg-[#FFF7F1] py-16 md:py-24 flex-1"
      >
        <div className="max-w-[900px] mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white border-2 border-[#141518] shadow-hard p-6 md:p-10"
          >
            <CopyRow
              label={g.lblIban}
              value={gift.iban}
              testId="regal-iban"
              copyText={g.copy}
              copiedText={g.copied}
            />
            <CopyRow
              label={g.lblRevolut}
              value={gift.revolut}
              testId="regal-revolut"
              copyText={g.copy}
              copiedText={g.copied}
            />
            <div className="py-5">
              <div className="font-condensed font-bold uppercase tracking-[0.25em] text-sm md:text-base text-[#F0311B]">
                {g.lblCash}
              </div>
              <div className="font-display font-semibold text-xl md:text-2xl mt-1 text-[#141518]">
                {gift.cash}
              </div>
            </div>
          </motion.div>

          <div className="mt-12 text-center">
            <a
              href="/"
              data-testid="regal-back-home"
              className="inline-flex items-center gap-3 bg-[#F0311B] text-white px-6 md:px-8 py-4 text-xs md:text-sm uppercase tracking-[0.28em] font-bold border-2 border-[#141518] shadow-[6px_6px_0_0_#141518] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[3px_3px_0_0_#141518] transition-all"
            >
              ← ariadnawojtek.cat
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RegalPage;
